import React, {
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import credentialsService from '../../services/credentialsService';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import api from '../../api';
import PopNotificationsContext from '../PopNotifications/PopNotifications.context';
import AuthContext from './Auth.context';
import CurrentUserProvider from '../CurrentUser';
import confirm from 'modules/confirm';
import { useTranslation } from 'react-i18next';
import resolveSocketService from 'services/socketService/resolveSocketService';
import { AuthBody, LoginForm, LoginResponse } from 'models/AuthBody';
import { SUCCESS_MESSAGES } from 'request-responses';
import useQueryParams from 'hooks/useQueryParams';
import { getEmailOrPhoneForLoginOrRegister } from 'utils';
import drCredentialsService from 'router/subrouters/DataRoom/services/drCredentialsService';
import NotificationsProvider from 'providers/Notifications';
import DocumentView from 'providers/DocumentView';
import mixpanelService from 'services/mixpanelService';
import {
  AUTH_ROUTE,
  BUSINESS_ROUTE,
  BUYING_PORTFOLIO_ROUTE,
  CREATE_DEAL_ROUTE,
  EDIT_DEAL_ROUTE,
  GLOBAL_MESSAGING,
  LCW_BASE_ROUTE,
  LOGIN_ROUTE,
  SEARCH_ROUTE,
  SELLING_PORTFOLIO_ROUTE,
  TWO_FA_ROUTE,
} from 'data/global';
import { User } from 'models/User';
import downloadService from 'services/downloadService';
import uniqid from 'uniqid';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import usePreviousLocation from './hooks/usePreviousLocation';
import refreshTokenService from 'services/refreshTokenService';
import { AuthEventTypes } from 'modules/bus';
import useBusEvent from 'hooks/useBusEvent';
import useFirst from 'hooks/useFirst';

const EXCLUDE_ROUTES = [
  LCW_BASE_ROUTE,
  CREATE_DEAL_ROUTE,
  EDIT_DEAL_ROUTE,
  SEARCH_ROUTE,
  AUTH_ROUTE,
];

const Auth: React.FC = (props) => {
  const { children } = props;

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!(credentialsService.token || credentialsService.refreshToken),
  );
  const [user, setUser] = useState<User>(credentialsService.currentUser);
  const [latestActivityMetricsLocation, setLatestActivityMetricsLocation] =
    useState<Location>();

  const { popSuccess, popError, popServerError, popInfo } = useContext(
    PopNotificationsContext,
  );

  const queryClient = useQueryClient();

  const { isFirst, onFirst, resetFirst } = useFirst();

  const isDeleted = useMemo(
    () => user?.hasActiveDeleteRequestAccount,
    [user?.hasActiveDeleteRequestAccount],
  );

  const isModalOpen = useRef(false);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const location = useLocation();

  const { previousLocation, updatePreviousLocation, navigateBack } =
    usePreviousLocation();

  const {
    params: { fullSlugName },
  } = useQueryParams();

  const getMixpanelProps = useGetMixpanelProps();

  const socialLogin = useCallback((body: Omit<AuthBody, 'user'>) => {
    credentialsService.saveAuthBody({ ...body, user: null });
    setIsLoggedIn(true);
  }, []);

  const onLoginReq = useCallback(
    (
      loginResponse: AuthBody,
      routeTo: string | Location,
      externalRoute?: boolean,
    ) => {
      const { user, token, refreshToken } = loginResponse;

      // DO NOT TOUCH THIS!
      // get ref to previous location before setting user and isLoggedIn prop
      const prevReroute = previousLocation;

      if (token || refreshToken) {
        credentialsService.saveAuthBody(loginResponse);
        setUser(user);
        setIsLoggedIn(true);
      }

      if (fullSlugName) {
        navigate(fullSlugName);
        return;
      }

      if (routeTo) {
        if (typeof routeTo === 'string') {
          if (externalRoute) window.location.href = routeTo;
          else navigate(routeTo);
        } else {
          const { pathname, ...routeToRest } = routeTo;

          if (externalRoute) window.location.href = pathname;
          else navigate(pathname, routeToRest);
        }
      } else {
        navigate(
          prevReroute
            ? prevReroute
            : user?.enterpriseMember || user?.hasSellingDeals
            ? SELLING_PORTFOLIO_ROUTE
            : user?.hasBuyingDeals
            ? BUYING_PORTFOLIO_ROUTE
            : BUSINESS_ROUTE,
        );
      }
    },
    [fullSlugName, navigate, previousLocation],
  );

  const isAuthBody = (prop: AuthBody | LoginResponse): prop is AuthBody =>
    !!(
      (prop as AuthBody).user &&
      (prop as AuthBody).token &&
      (prop as AuthBody).refreshToken
    );

  const loginRequest = useCallback(
    async (values: LoginForm, routeTo?: string | Location) => {
      const { email, emailOrPhone, ...rest } = values;
      const requestData = {
        ...rest,
        ...getEmailOrPhoneForLoginOrRegister({ email, emailOrPhone }),
      };

      try {
        const { data: loginResponse } = await api.auth.login(requestData);

        if (isAuthBody(loginResponse)) {
          onLoginReq(loginResponse, routeTo);
          return;
        }

        credentialsService.saveTwoFa(loginResponse);
        navigate(TWO_FA_ROUTE);
      } catch (e) {
        popServerError(e);
      }
    },
    [navigate, onLoginReq, popServerError],
  );

  const logoutRequest = useCallback(
    (routeTo?: string, isExternal = false) => {
      mixpanelService.logout(getMixpanelProps());
      mixpanelService.reset();

      localStorage.clear();
      credentialsService.removeAuthBody();
      drCredentialsService.removeAuthBody();
      queryClient.removeQueries();

      if (routeTo) {
        if (isExternal) {
          window.location.href = routeTo;
        } else {
          navigate(routeTo);
        }
      }

      setIsLoggedIn(false);
      setUser(null);
    },
    [getMixpanelProps, navigate, queryClient],
  );

  const switchUser = useCallback(
    (authBody: AuthBody, routeTo?: string) => {
      const { user: newUser, token, refreshToken } = authBody;

      if (!token || !refreshToken) return;

      mixpanelService.reset();
      downloadService.downloadUniqueId = uniqid();
      queryClient.removeQueries();

      credentialsService.saveAuthBody(authBody);
      if (!newUser) resetFirst();
      setUser(newUser);
      setIsLoggedIn(true);

      resolveSocketService.reconnect();

      if (routeTo) navigate(routeTo, { replace: true });
    },
    [navigate, queryClient, resetFirst],
  );

  const value = useMemo(
    () => ({
      isLoggedIn,
      previousLocation,
      latestActivityMetricsLocation,
      navigateBack,
      loginRequest,
      socialLogin,
      logoutRequest,
      onLoginReq,
      switchUser,
      updateLatestActivityMetricsLocation: setLatestActivityMetricsLocation,
    }),
    [
      isLoggedIn,
      previousLocation,
      latestActivityMetricsLocation,
      navigateBack,
      loginRequest,
      socialLogin,
      logoutRequest,
      onLoginReq,
      switchUser,
    ],
  );

  const onForbiddenSignal = useCallback(() => {
    popError(t('forbiddenRedirectMessage'));
    navigate(BUSINESS_ROUTE, { replace: true });
  }, [navigate, popError, t]);

  const onLogoutSignal = useCallback(() => {
    popError(t('sessionExpired'));
    refreshTokenService.reinit();
    logoutRequest(LOGIN_ROUTE);
  }, [logoutRequest, popError, t]);

  useBusEvent(AuthEventTypes.FORBIDDEN_SIGNAL, onForbiddenSignal);
  useBusEvent(AuthEventTypes.LOGOUT_SIGNAL, onLogoutSignal);

  useEffect(() => {
    if (isLoggedIn && user?._id) {
      mixpanelService.identify(user._id);

      if (!downloadService.downloadUniqueId) {
        downloadService.downloadUniqueId = uniqid();
      }
    }
  }, [isLoggedIn, user?._id]);

  useEffect(() => {
    if (!isLoggedIn || (user && !isFirst)) return;

    onFirst(async () => {
      try {
        const {
          data: { user },
        } = await api.users.getCurrentUser();

        setUser(user);
      } catch (e) {
        popServerError(e, t('previousSessionExpired'));
        logoutRequest();
      }
    });
  }, [isFirst, isLoggedIn, logoutRequest, onFirst, popServerError, t, user]);

  useEffect(() => {
    if (user) {
      credentialsService.currentUser = user;
      credentialsService.currentUserId = user._id;
    } else {
      resetFirst();
    }
  }, [resetFirst, user]);

  useEffect(() => {
    if (!isLoggedIn) return;

    resolveSocketService.connect();
    return () => {
      resolveSocketService.disconnect();
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!EXCLUDE_ROUTES.some((route) => location.pathname.includes(route)))
      updatePreviousLocation(location);
  }, [location, updatePreviousLocation]);

  useEffect(() => {
    if (location.pathname.includes('messaging'))
      updatePreviousLocation({ ...location, pathname: GLOBAL_MESSAGING });
  }, [location, updatePreviousLocation]);

  if (!isLoggedIn) {
    return (
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
  }

  if (isDeleted) {
    !isModalOpen.current &&
      (async () => {
        isModalOpen.current = true;
        if (
          await confirm(
            {
              title: t('cancelAccountDeletion'),
              content: t('cancelAccountDeletionMessage'),
              overlayCloses: false,
            },
            true,
          )
        ) {
          try {
            await api.users.deleteAccountRevert();
            setUser((prev) => ({
              ...prev,
              hasActiveDeleteRequestAccount: false,
            }));
            popSuccess(t(SUCCESS_MESSAGES.REVERT_ACCOUNT_DELETION));
          } catch (e) {
            popServerError(e);
          } finally {
            isModalOpen.current = false;
          }
        } else {
          logoutRequest(LOGIN_ROUTE);
          popInfo({ content: t('loggedOutAccDel') });
          isModalOpen.current = false;
        }
      })();
    return <></>;
  }

  return (
    <AuthContext.Provider value={value}>
      <CurrentUserProvider currentUser={user} setCurrentUser={setUser}>
        <DocumentView>
          <NotificationsProvider key={user?._id}>
            {children}
          </NotificationsProvider>
        </DocumentView>
      </CurrentUserProvider>
    </AuthContext.Provider>
  );
};

export default Auth;
